import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCopy } from '@fortawesome/free-solid-svg-icons';
import { RequestLanguage } from 'data/parseShiplabOpenApi';

export type ApiCodePanelVariant = 'request' | 'response' | 'snippet';

const PANEL_STYLES: Record<
  'request' | 'response',
  { headerBg: string; headerColor: string; bodyBg: string; borderColor: string }
> = {
  request: {
    headerBg: '#1e3a5f',
    headerColor: '#e0f2fe',
    bodyBg: '#0f172a',
    borderColor: '#334155',
  },
  response: {
    headerBg: '#14532d',
    headerColor: '#dcfce7',
    bodyBg: '#052e16',
    borderColor: '#166534',
  },
};

const REQUEST_LANGUAGE_LABELS: Record<RequestLanguage, string> = {
  bash: 'cURL',
  python: 'Python',
  js: 'JS',
};

const REQUEST_LANGUAGES = Object.keys(REQUEST_LANGUAGE_LABELS) as RequestLanguage[];

type LanguageMenuProps = {
  value: RequestLanguage;
  headerColor: string;
  onChange: (language: RequestLanguage) => void;
  onOpenChange: (open: boolean) => void;
};

const LanguageMenu: React.FC<LanguageMenuProps> = ({
  value,
  headerColor,
  onChange,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, onOpenChange]);

  const setMenuOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange(next);
  };

  return (
    <div ref={rootRef} className="position-relative">
      <button
        type="button"
        className="border-0 rounded-1 px-2 py-0 d-inline-flex align-items-center gap-1"
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: headerColor,
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setMenuOpen(!open)}
      >
        {REQUEST_LANGUAGE_LABELS[value]}
        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.5rem', opacity: 0.85 }} />
      </button>
      {open && (
        <div
          role="listbox"
          className="position-absolute border rounded-1 shadow-sm"
          style={{
            top: 'calc(100% + 2px)',
            left: 0,
            zIndex: 50,
            backgroundColor: '#ffffff',
            minWidth: '4.25rem',
            padding: '0.125rem 0',
          }}
        >
          {REQUEST_LANGUAGES.map((key) => {
            const selected = value === key;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={selected}
                className="d-block w-100 border-0 text-start"
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.2rem 0.55rem',
                  color: selected ? '#ffffff' : '#0f172a',
                  backgroundColor: selected ? '#2563eb' : 'transparent',
                }}
                onMouseEnter={(event) => {
                  if (!selected) {
                    event.currentTarget.style.backgroundColor = '#f1f5f9';
                  }
                }}
                onMouseLeave={(event) => {
                  if (!selected) {
                    event.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onClick={() => {
                  onChange(key);
                  setMenuOpen(false);
                }}
              >
                {REQUEST_LANGUAGE_LABELS[key]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

type ApiCodePanelProps =
  | {
      label: string;
      variant: 'request';
      examples: Record<RequestLanguage, string>;
    }
  | {
      label: string;
      variant: 'response' | 'snippet';
      code: string;
    };

const ApiCodePanel: React.FC<ApiCodePanelProps> = (props) => {
  const [language, setLanguage] = useState<RequestLanguage>('bash');
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const styles =
    props.variant === 'response' ? PANEL_STYLES.response : PANEL_STYLES.request;
  const code =
    props.variant === 'request' ? props.examples[language] : props.code;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="rounded-2 w-100"
      style={{
        border: `1px solid ${styles.borderColor}`,
        position: 'relative',
        zIndex: menuOpen ? 30 : undefined,
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center px-2"
        style={{
          backgroundColor: styles.headerBg,
          borderTopLeftRadius: '0.375rem',
          borderTopRightRadius: '0.375rem',
          minHeight: '1.625rem',
          paddingTop: '0.25rem',
          paddingBottom: '0.25rem',
        }}
      >
        <span
          className="fw-semi-bold text-uppercase"
          style={{
            fontSize: '0.6875rem',
            letterSpacing: '0.08em',
            color: styles.headerColor,
          }}
        >
          {props.label}
        </span>
        <div className="d-flex align-items-center" style={{ gap: '0.65rem', minHeight: '1.125rem' }}>
          {props.variant === 'request' ? (
            <LanguageMenu
              value={language}
              headerColor={styles.headerColor}
              onChange={setLanguage}
              onOpenChange={setMenuOpen}
            />
          ) : (
            <span style={{ width: '3.5rem' }} aria-hidden="true" />
          )}
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            style={{ fontSize: '0.75rem', color: styles.headerColor, opacity: 0.85 }}
            onClick={(e) => {
              handleCopy();
              e.currentTarget.blur();
            }}
          >
            <FontAwesomeIcon icon={faCopy} className="me-1" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <pre
        className="mb-0 px-2 py-2 font-monospace"
        style={{
          fontSize: '0.7rem',
          lineHeight: 1.55,
          minHeight: '5.5rem',
          backgroundColor: styles.bodyBg,
          color: '#e2e8f0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          borderBottomLeftRadius: '0.375rem',
          borderBottomRightRadius: '0.375rem',
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
};

export default ApiCodePanel;
